import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  forwardRef,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { NgComponentOutlet } from '@angular/common';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { LucideChevronDown, LucidePlus } from '@lucide/angular';
import { FieldBase } from '../shared/field-base';
import { FieldErrorComponent } from '../shared/field-error.component';

/**
 * Combobox free-text: input dengan saran dropdown yang bisa dipilih cepat,
 * tetapi user bebas mengetik nilai baru. Setiap opsi terpilih menjadi nilai
 * kontrol; teks yang diketik tetap diterima walau tidak ada di daftar.
 *
 * Aksesibilitas (WAI-ARIA combobox + listbox pattern):
 * navigasi keyboard penuh, `aria-activedescendant`, klik-luar tutup.
 */
@Component({
  selector: 'ds-autocomplete',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgComponentOutlet, FieldErrorComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AutocompleteComponent),
      multi: true,
    },
  ],
  host: {
    '(document:mousedown)': 'onDocumentMousedown($event)',
  },
  template: `
    <div class="ds-field">
      <label class="ds-field__label" [for]="fieldId">
        {{ label() }}
        @if (required()) {
          <span class="ds-field__required" aria-hidden="true">*</span>
        }
      </label>
      @if (helperText()) {
        <p class="ds-field__helper" [id]="fieldId + '-helper'">{{ helperText() }}</p>
      }
      <div class="ac" #root>
        <input
          class="ds-input ac__input"
          [id]="fieldId"
          type="text"
          [value]="value() ?? ''"
          [disabled]="disabled()"
          [required]="required()"
          [placeholder]="placeholder() ?? ''"
          [attr.autocomplete]="'off'"
          role="combobox"
          [attr.aria-expanded]="open() && optionCount() > 0"
          [attr.aria-controls]="listboxId"
          aria-autocomplete="list"
          [attr.aria-activedescendant]="activeOptionId()"
          [attr.aria-invalid]="errorText() ? 'true' : null"
          [attr.aria-describedby]="describedBy()"
          (input)="onInput($event)"
          (focus)="onFocus()"
          (keydown)="onKeydown($event)"
        />
        <span class="ac__chevron" aria-hidden="true">
          <ng-container
            [ngComponentOutlet]="chevron"
            [ngComponentOutletInputs]="{ size: 24, strokeWidth: 2.25 }"
          />
        </span>

        @if (open() && optionCount() > 0) {
          <ul class="ac__listbox" [id]="listboxId" role="listbox">
            @for (opt of filtered(); track opt; let i = $index) {
              <li
                [id]="optionId(i)"
                class="ac__option"
                role="option"
                [class.ac__option--active]="i === activeIndex()"
                [attr.aria-selected]="i === activeIndex()"
                (mousedown)="preventBlur($event)"
                (click)="selectOption(opt)"
              >
                {{ opt }}
              </li>
            }
            @if (showCreateOption()) {
              <li
                [id]="optionId(filtered().length)"
                class="ac__option ac__option--create"
                role="option"
                [class.ac__option--active]="filtered().length === activeIndex()"
                [attr.aria-selected]="filtered().length === activeIndex()"
                (mousedown)="preventBlur($event)"
                (click)="selectOption(createValue())"
              >
                <span class="ac__create-icon" aria-hidden="true">
                  <ng-container
                    [ngComponentOutlet]="plusIcon"
                    [ngComponentOutletInputs]="{ size: 18, strokeWidth: 2.5 }"
                  />
                </span>
                <span>Tambah "<strong>{{ createValue() }}</strong>"</span>
              </li>
            }
          </ul>
        }
      </div>

      @if (errorText()) {
        <ds-field-error [errorId]="fieldId + '-error'">{{ errorText() }}</ds-field-error>
      }
    </div>
  `,
  styles: `
    .ac {
      position: relative;
    }

    .ac__input {
      padding-right: var(--space-7);
      cursor: text;

      &:disabled {
        cursor: not-allowed;
      }
    }

    .ac__chevron {
      position: absolute;
      right: var(--space-4);
      top: 50%;
      transform: translateY(-50%);
      display: inline-flex;
      pointer-events: none;
      color: var(--color-text-secondary);
    }

    .ac__listbox {
      position: absolute;
      top: 100%;
      left: 0;
      right: 0;
      list-style: none;
      margin: var(--space-1) 0 0;
      padding: var(--space-1);
      max-height: 16rem;
      overflow-y: auto;
      background: var(--color-surface);
      border: 1px solid var(--color-border);
      border-radius: var(--radius-md);
      box-shadow: var(--shadow-md);
      z-index: var(--z-dropdown, 20);
    }

    .ac__option {
      display: flex;
      align-items: center;
      gap: var(--space-2);
      padding: var(--space-2) var(--space-3);
      border-radius: var(--radius-sm);
      cursor: pointer;
      color: var(--color-text);
      font-size: var(--text-md);

      &:hover {
        background: oklch(93.2% 0.032 255.585);
      }

      &--active {
        background: var(--color-bg-subtle);
      }
    }

    .ac__option--create {
      border-top: 1px solid var(--color-border);
      border-top-left-radius: 0;
      border-top-right-radius: 0;
      margin-top: var(--space-1);
      padding-top: var(--space-2);
      color: var(--color-text-secondary);

      strong {
        color: var(--color-text);
      }
    }

    .ac__create-icon {
      display: inline-flex;
      color: var(--color-primary);
    }
  `,
})
export class AutocompleteComponent extends FieldBase<string> {
  options = input<string[]>([]);
  placeholder = input<string | undefined>(undefined);
  allowCreate = input(true);

  readonly valueChange = output<string>();
  readonly optionSelected = output<string | null>();

  private readonly hostRef = inject(ElementRef<HTMLElement>);

  protected readonly chevron = LucideChevronDown;
  protected readonly plusIcon = LucidePlus;

  protected readonly open = signal(false);
  protected readonly activeIndex = signal(-1);

  protected readonly listboxId = `${this.fieldId}-listbox`;

  protected readonly filtered = computed(() => {
    const q = (this.value() ?? '').trim().toLowerCase();
    if (!q) return [...this.options()];
    return this.options().filter((o) => o.toLowerCase().includes(q));
  });

  protected readonly hasExactMatch = computed(() => {
    const q = (this.value() ?? '').trim().toLowerCase();
    return q.length > 0 && this.options().some((o) => o.toLowerCase() === q);
  });

  protected readonly showCreateOption = computed(() => {
    if (!this.allowCreate()) return false;
    const q = (this.value() ?? '').trim();
    return q.length > 0 && !this.hasExactMatch();
  });

  protected readonly optionCount = computed(
    () => this.filtered().length + (this.showCreateOption() ? 1 : 0),
  );

  protected readonly createValue = computed(() => (this.value() ?? '').trim());

  protected optionId(i: number): string {
    return `${this.fieldId}-opt-${i}`;
  }

  protected activeOptionId(): string | null {
    const i = this.activeIndex();
    if (i < 0) return null;
    return this.optionId(i);
  }

  protected onInput(event: Event): void {
    const next = (event.target as HTMLInputElement).value;
    this.value.set(next);
    this.onChange(next);
    this.activeIndex.set(-1);
    this.open.set(true);
    this.valueChange.emit(next);
    this.optionSelected.emit(null);
  }

  protected onFocus(): void {
    this.open.set(true);
  }

  protected onKeydown(event: KeyboardEvent): void {
    const count = this.optionCount();
    switch (event.key) {
      case 'ArrowDown':
        if (count === 0) return;
        event.preventDefault();
        if (!this.open()) {
          this.open.set(true);
          return;
        }
        this.activeIndex.update((i) => Math.min(i + 1, count - 1));
        break;
      case 'ArrowUp':
        if (count === 0) return;
        event.preventDefault();
        if (!this.open()) {
          this.open.set(true);
          return;
        }
        this.activeIndex.update((i) => Math.max(i - 1, 0));
        break;
      case 'Enter':
        if (!this.open()) return;
        event.preventDefault();
        this.commitActive();
        break;
      case 'Escape':
        if (this.open()) {
          event.preventDefault();
          this.close();
        }
        break;
      case 'Tab':
        if (this.open()) this.close();
        break;
    }
  }

  protected preventBlur(event: MouseEvent): void {
    event.preventDefault();
  }

  protected selectOption(label: string): void {
    this.value.set(label);
    this.onChange(label);
    this.optionSelected.emit(label);
    this.close();
  }

  protected onDocumentMousedown(event: MouseEvent): void {
    if (!this.open()) return;
    const target = event.target as Node | null;
    if (target && !this.hostRef.nativeElement.contains(target)) {
      this.close();
    }
  }

  private commitActive(): void {
    const i = this.activeIndex();
    const filtered = this.filtered();
    if (i >= 0 && i < filtered.length) {
      this.selectOption(filtered[i]);
      return;
    }
    if (this.showCreateOption() && i === filtered.length) {
      this.selectOption(this.createValue());
      return;
    }
    this.close();
  }

  private close(): void {
    this.open.set(false);
    this.activeIndex.set(-1);
    this.onTouched();
  }
}
